/**
 * Step 2: BN Number Lookup or Manual Entry
 * If user provided BN in step 1, auto-lookup
 * Otherwise, offer manual entry
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, XCircle, Search } from 'lucide-react';
import apiClient from '@/lib/apiClient';

const PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'YT', name: 'Yukon' }
];

export default function BNLookupStep({ userData, onComplete, onBack }) {
  const [mode, setMode] = useState(userData?.has_bn_number ? 'lookup' : 'manual');
  const [loading, setLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [bnNumber, setBnNumber] = useState(userData?.bn_number || '');
  const [manualData, setManualData] = useState({
    legal_name: '',
    city: '',
    province: ''
  });

  // Auto-lookup if BN was provided in step 1
  useEffect(() => {
    if (userData?.bn_number && mode === 'lookup') {
      handleLookup(userData.bn_number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLookup = async (bn) => {
    if (!bn) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/onboarding/lookup/${bn}`);
      setLookupResult(response.data);

      if (response.data.found) {
        // Auto-advance if organization found
        setTimeout(() => {
          onComplete({
            mode: 'lookup',
            organization: response.data.organization
          });
        }, 1500);
      }
    } catch (err) {
      console.error('Lookup error:', err);
      setLookupResult({
        found: false,
        message: 'Error looking up organization. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    onComplete({
      mode: 'manual',
      organization: {
        ...manualData,
        bn_number: null
      }
    });
  };

  const handleManualChange = (e) => {
    setManualData({
      ...manualData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          {mode === 'lookup' ? 'Find Your Organization' : 'Enter Organization Details'}
        </h2>
        <p className="text-slate-400">
          {mode === 'lookup'
            ? 'We\'ll auto-fill your organization details from our database'
            : 'Provide your organization information manually'}
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          type="button"
          variant={mode === 'lookup' ? 'default' : 'outline'}
          onClick={() => setMode('lookup')}
          className={mode === 'lookup' ? 'bg-blue-500' : 'border-slate-600 text-slate-300'}
        >
          <Search className="w-4 h-4 mr-2" />
          Lookup by BN Number
        </Button>
        <Button
          type="button"
          variant={mode === 'manual' ? 'default' : 'outline'}
          onClick={() => setMode('manual')}
          className={mode === 'manual' ? 'bg-blue-500' : 'border-slate-600 text-slate-300'}
        >
          Manual Entry
        </Button>
      </div>

      {/* Lookup Mode */}
      {mode === 'lookup' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bn_lookup" className="text-white">
              CRA Business Number
            </Label>
            <div className="flex gap-2">
              <Input
                id="bn_lookup"
                type="text"
                value={bnNumber}
                onChange={(e) => setBnNumber(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="123456789RR0001"
                maxLength={15}
              />
              <Button
                type="button"
                onClick={() => handleLookup(bnNumber)}
                disabled={loading || !bnNumber}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Search'
                )}
              </Button>
            </div>
          </div>

          {/* Lookup Result */}
          {lookupResult && (
            <Alert className={lookupResult.found ? 'bg-green-500/10 border-green-500/50' : 'bg-yellow-500/10 border-yellow-500/50'}>
              {lookupResult.found ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-yellow-500" />
              )}
              <AlertDescription className={lookupResult.found ? 'text-green-400' : 'text-yellow-400'}>
                {lookupResult.message}
              </AlertDescription>
            </Alert>
          )}

          {lookupResult?.found && (
            <div className="bg-slate-700/50 rounded-lg p-6 space-y-3">
              <h3 className="text-white font-semibold mb-4">Organization Found:</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-400">Legal Name</div>
                  <div className="text-white font-medium">{lookupResult.organization.legal_name}</div>
                </div>
                <div>
                  <div className="text-slate-400">BN Number</div>
                  <div className="text-white font-medium">{lookupResult.organization.bn_number}</div>
                </div>
                <div>
                  <div className="text-slate-400">City</div>
                  <div className="text-white font-medium">{lookupResult.organization.city}</div>
                </div>
                <div>
                  <div className="text-slate-400">Province</div>
                  <div className="text-white font-medium">{lookupResult.organization.province}</div>
                </div>
                {lookupResult.organization.revenue && (
                  <div>
                    <div className="text-slate-400">Revenue (Latest)</div>
                    <div className="text-white font-medium">
                      ${lookupResult.organization.revenue.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === 'manual' && (
        <form onSubmit={handleManualSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="legal_name" className="text-white">
              Organization Legal Name *
            </Label>
            <Input
              id="legal_name"
              name="legal_name"
              type="text"
              required
              value={manualData.legal_name}
              onChange={handleManualChange}
              className="bg-slate-700 border-slate-600 text-white"
              placeholder="e.g., Toronto Community Foundation"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city" className="text-white">
                City *
              </Label>
              <Input
                id="city"
                name="city"
                type="text"
                required
                value={manualData.city}
                onChange={handleManualChange}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Toronto"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="province" className="text-white">
                Province *
              </Label>
              <Select
                value={manualData.province}
                onValueChange={(value) =>
                  setManualData({ ...manualData, province: value })
                }
                required
              >
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue placeholder="Select province" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCES.map((prov) => (
                    <SelectItem key={prov.code} value={prov.code}>
                      {prov.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              className="flex-1 border-slate-600 text-slate-300"
            >
              Back
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
            >
              Continue
            </Button>
          </div>
        </form>
      )}

      {/* Back button for lookup mode */}
      {mode === 'lookup' && !lookupResult?.found && (
        <div className="flex gap-3 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-slate-600 text-slate-300"
          >
            Back
          </Button>
        </div>
      )}
    </div>
  );
}
